import { IconButton, ToolsIcon } from "@carbon6/c6-frontend-design";
import { useCallback, useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { canModifyEmbodiment } from "@/app";
import { GnbMenu } from "@/components/base";
import { EmbodimentComposerFlow } from "@/components/embodiment";
import { FilterBar, FilterSearchInput, type FilterBarChip } from "@/components/filter";
import {
  PageBase,
  type PageBaseContext,
  type PageBaseProps,
  type PageBaseTabs,
} from "@/page/base/PageBase";
import { EmbodimentCatalogTab } from "./components/EmbodimentCatalogTab";
import { PartsCatalogTab } from "./components/PartsCatalogTab";
import {
  EMPTY_EMBODIMENT_FILTERS,
  embodimentFilterChips,
  readEmbodimentFilters,
  writeEmbodimentFilters,
  type EmbodimentFilterState,
} from "./embodimentFilters";
import { isPartKind, partKindMetas, partKindOrder } from "./partsCatalog";

const embodimentsTabValue = "embodiments";

// 탭 선택을 담는 쿼리 키. router 의 /embodiments/end-effectors·/tips 별칭도 이 키로 리다이렉트한다.
const tabParamKey = "tab";

const embodimentTabItems: PageBaseTabs["items"] = [
  { value: embodimentsTabValue, label: "Embodiments" },
  ...partKindOrder.map((kind) => ({ value: kind, label: partKindMetas[kind].plural })),
];

// Embodiment와 EndEffector/Tip 카탈로그를 Operations 그룹의 단일 페이지로 노출한다.
// 탭은 Embodiments(기본)와 부품(EndEffector/Tip)이다. 목록은 공개이고, mutation 은 admin 만 한다.
// 탭 선택과 카탈로그 검색은 URL(?tab=·?search=)이 소유한다. 그래서 뒤로가기와 새로고침에도 유지된다.
export function EmbodimentPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get(tabParamKey);
  const currentTab =
    tabParam !== null && embodimentTabItems.some((item) => item.value === tabParam)
      ? tabParam
      : embodimentsTabValue;
  const filters = useMemo(() => readEmbodimentFilters(searchParams), [searchParams]);
  const handleFiltersChange = useCallback(
    (next: EmbodimentFilterState) => {
      setSearchParams((prev) => writeEmbodimentFilters(prev, next), { replace: true });
    },
    [setSearchParams],
  );

  const handleTabChange = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(tabParamKey, value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const tabs = useMemo<PageBaseTabs>(
    () => ({
      ariaLabel: "Embodiment 섹션",
      items: embodimentTabItems,
      value: currentTab,
      onValueChange: handleTabChange,
    }),
    [currentTab, handleTabChange],
  );

  return (
    <EmbodimentPageView
      pageTitle="Embodiments"
      currentMenu={GnbMenu.Embodiment}
      // 카탈로그 관리 화면이므로 운영자 이상에게만 연다. 읽기 API 는 member 에게도 열려 있다
      // (데이터셋 필터와 배지가 그 응답을 쓴다). 여기서 막는 것은 관리 화면뿐이다.
      requiredRoles={["admin"]}
      tabs={tabs}
      scrollable
      // 필터 바가 이미 헤더와 본문을 구분하는 탭에서는 tabpanel 상단 여백을 더하지 않는다.
      bodyFlushTop={currentTab === embodimentsTabValue}
      filters={filters}
      onFiltersChange={handleFiltersChange}
    />
  );
}

type EmbodimentPageViewProps = PageBaseProps & {
  filters: EmbodimentFilterState;
  onFiltersChange: (next: EmbodimentFilterState) => void;
};

type EmbodimentPageViewState = {
  // 헤더의 새 Embodiment 컴포저 열림 여부. 진입점이 헤더이므로 어느 탭에서든 열 수 있다.
  composerOpen: boolean;
  // 컴포저 적용 횟수. Embodiment 카탈로그 탭이 이 신호로 목록을 다시 조회한다.
  catalogRefreshSeq: number;
};

class EmbodimentPageView extends PageBase<EmbodimentPageViewProps, EmbodimentPageViewState> {
  state: EmbodimentPageViewState = { composerOpen: false, catalogRefreshSeq: 0 };

  private readonly openComposer = (): void => {
    this.setState({ composerOpen: true });
  };

  private readonly closeComposer = (): void => {
    this.setState({ composerOpen: false });
  };

  // 컴포저가 생성 결과를 직접 저장한다. 그래서 여기서는 컴포저를 닫고 목록 재조회 신호만 증가시킨다.
  private readonly handleComposerApply = (): void => {
    this.setState((state) => ({
      composerOpen: false,
      catalogRefreshSeq: state.catalogRefreshSeq + 1,
    }));
  };

  // 제목 우측의 새 Embodiment 버튼. 페이지 대표 엔티티의 생성 진입점이므로 탭과 무관하게 헤더에 둔다.
  protected override renderHeaderActions(context: PageBaseContext): ReactNode {
    return (
      <IconButton
        text="새 Embodiment"
        variant="solid"
        disabled={!canModifyEmbodiment(context.currentUserRole)}
        onClick={this.openComposer}
      >
        <ToolsIcon />
      </IconButton>
    );
  }

  // 탭 줄 아래의 필터 바. 카탈로그 탭에서만 표시한다. 부품 카탈로그는 서버 검색 축이 없다(rules.md §17).
  protected override renderBelowTabs({ currentTab }: PageBaseContext): ReactNode {
    if (currentTab !== null && isPartKind(currentTab)) {
      return null;
    }
    const { filters, onFiltersChange } = this.props;
    const chips: FilterBarChip[] = embodimentFilterChips(filters).map((chip) => ({
      key: chip.axis,
      label: chip.label,
      removeLabel: `${chip.label} 해제`,
      onRemove: () => onFiltersChange({ ...filters, [chip.axis]: "" }),
    }));
    return (
      <FilterBar
        ariaLabel="Embodiment 필터"
        search={
          <FilterSearchInput
            value={filters.search}
            ariaLabel="Embodiment 검색"
            placeholder="Embodiment를 검색해 보세요."
            onCommit={(search) => onFiltersChange({ ...filters, search })}
          />
        }
        chips={chips}
        onClearAll={() => onFiltersChange(EMPTY_EMBODIMENT_FILTERS)}
      />
    );
  }

  protected override renderContent({
    currentTab,
    currentUserRole,
    currentUserPermissions,
  }: PageBaseContext): ReactNode {
    // 컴포저는 헤더 진입점과 한 쌍이므로 탭 분기 밖에서 그린다. 부품 탭에서 열어도 동작한다.
    const composer = this.state.composerOpen ? (
      <EmbodimentComposerFlow
        open
        role={currentUserRole}
        currentTip={null}
        onClose={this.closeComposer}
        onApply={this.handleComposerApply}
      />
    ) : null;
    if (currentTab !== null && isPartKind(currentTab)) {
      return (
        <>
          <PartsCatalogTab
            key={currentTab}
            kind={currentTab}
            role={currentUserRole}
            permissions={currentUserPermissions}
          />
          {composer}
        </>
      );
    }
    return (
      <>
        <EmbodimentCatalogTab
          role={currentUserRole}
          permissions={currentUserPermissions}
          refreshSeq={this.state.catalogRefreshSeq}
          searchQuery={this.props.filters.search}
        />
        {composer}
      </>
    );
  }
}
